import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Flex, Input, List, Modal, Popconfirm, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { DESTROY_APP_CONNECTION, STORE_APP_CONNECTION } from '../../user/permission'
import { useAppCatalog, useAppConnections, useCompleteApp, useDisconnectApp, useOpenConnection } from '../hooks'
import { AppConnection, AppToolkit } from '../types'
import { AppIcon } from './app-icon'
import { LoadMoreOnScroll } from './load-more-on-scroll'

// The sign-in provider returns to this page with these query parameters.
const redirectParams = ['connection', 'state', 'status', 'connectedAccountId', 'connected_account_id']

function removeRedirectParams() {
    const url = new URL(window.location.href)

    for (const key of redirectParams) {
        url.searchParams.delete(key)
    }

    window.history.replaceState(window.history.state, '', url)
}

export function AppConnections() {
    const { t } = useTranslation()
    const { can } = useAuth()
    const [awaiting, setAwaiting] = useState(false)
    const connections = useAppConnections({ poll: awaiting })
    const complete = useCompleteApp()
    const attempted = useRef(false)
    const [catalogOpen, setCatalogOpen] = useState(false)

    // Finish the connection when the sign-in tab returns here.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const id = params.get('connection')
        const state = params.get('state')

        if (!id || !state || attempted.current || !can(STORE_APP_CONNECTION)) {
            return
        }

        attempted.current = true
        void complete
            .mutateAsync({ id, state })
            .then(removeRedirectParams)
            .catch(() => undefined)
    }, [complete, can])

    useEffect(() => {
        const hasPending = connections.data?.some((connection) => connection.status === 'pending')

        if (awaiting && !hasPending) {
            setAwaiting(false)
        }
    }, [awaiting, connections.data])

    return (
        <Card
            title={t('Connected apps')}
            extra={
                can(STORE_APP_CONNECTION) && <Button onClick={() => setCatalogOpen(true)}>{t('Connect app')}</Button>
            }
        >
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t(
                        'Connect company accounts so custom conversations can use them, for example to check a calendar.'
                    )}
                </Typography.Text>
                {complete.isPending && (
                    <Alert
                        type="info"
                        message={t('Connecting app')}
                    />
                )}
                {complete.isSuccess && (
                    <Alert
                        type="success"
                        message={t('App connected. You can close this tab.')}
                    />
                )}
                {complete.isError && (
                    <Alert
                        type="error"
                        message={t('Could not connect app. Start a new connection and try again.')}
                    />
                )}
                {connections.isError && (
                    <Alert
                        type="error"
                        message={t('Could not load connected apps')}
                    />
                )}
                <List
                    loading={connections.isPending}
                    // Disconnected apps stay stored for campaign history but are not shown here.
                    dataSource={connections.data?.filter((connection) => connection.status !== 'disconnected') ?? []}
                    renderItem={(connection) => (
                        <ConnectedAppItem
                            connection={connection}
                            onReconnect={() => setAwaiting(true)}
                        />
                    )}
                />
            </Flex>
            <Modal
                title={t('Connect app')}
                open={catalogOpen}
                onCancel={() => setCatalogOpen(false)}
                destroyOnHidden
                footer={null}
            >
                <AppCatalog
                    connections={connections.data ?? []}
                    onConnecting={() => setAwaiting(true)}
                />
            </Modal>
        </Card>
    )
}

function ConnectedAppItem({ connection, onReconnect }: { connection: AppConnection; onReconnect: () => void }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const disconnect = useDisconnectApp()
    const reconnect = useOpenConnection()
    const statusText = connection.status === 'connected' ? t('Connected') : t('Sign-in not finished')

    // A new sign-in replaces the unfinished connection.
    const connectAgain = () =>
        void reconnect
            .open(connection.toolkit)
            .then(() => {
                onReconnect()

                return disconnect.mutateAsync(connection.id)
            })
            .catch(() => undefined)

    const actions = () => {
        if (connection.status === 'pending') {
            return [
                can(STORE_APP_CONNECTION) && (
                    <Button
                        key="reconnect"
                        loading={reconnect.isPending}
                        onClick={connectAgain}
                    >
                        {t('Connect again')}
                    </Button>
                ),
                can(DESTROY_APP_CONNECTION) && (
                    <Button
                        key="remove"
                        loading={disconnect.isPending}
                        onClick={() => disconnect.mutate(connection.id)}
                    >
                        {t('Remove')}
                    </Button>
                ),
            ]
        }

        if (connection.status === 'connected' && can(DESTROY_APP_CONNECTION)) {
            return [
                <Popconfirm
                    key="disconnect"
                    title={t('Disconnect app? Campaigns will lose access to its actions.')}
                    onConfirm={() => disconnect.mutateAsync(connection.id)}
                >
                    <Button loading={disconnect.isPending}>{t('Disconnect')}</Button>
                </Popconfirm>,
            ]
        }

        return []
    }

    return (
        <List.Item actions={actions().filter(Boolean)}>
            <List.Item.Meta
                avatar={
                    <AppIcon
                        name={connection.name}
                        toolkit={connection.toolkit}
                    />
                }
                title={connection.name}
                description={
                    reconnect.isError ? t('Could not connect app. Start a new connection and try again.') : statusText
                }
            />
        </List.Item>
    )
}

type AppCatalogProps = {
    connections: AppConnection[]
    onConnecting: (connectionId: string) => void
}

export function AppCatalog({ connections, onConnecting }: AppCatalogProps) {
    const { t } = useTranslation()
    const [searchText, setSearchText] = useState('')
    const search = useDebouncedSearch(searchText)
    const catalog = useAppCatalog(search)
    const connection = useOpenConnection()
    const connected = new Set(connections.filter((app) => app.status === 'connected').map((app) => app.toolkit))

    const connect = (toolkit: string) =>
        void connection
            .open(toolkit)
            .then((result) => onConnecting(result.id))
            .catch(() => undefined)

    return (
        <Flex
            vertical
            gap="middle"
        >
            <Input.Search
                placeholder={t('Search apps')}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                allowClear
            />
            <Typography.Text type="secondary">
                {t('Sign-in opens in a new tab. You can continue here.')}
            </Typography.Text>
            {connection.isError && (
                <Alert
                    type="error"
                    message={t('Could not connect app. Start a new connection and try again.')}
                />
            )}
            {connection.redirectUrl && (
                <Typography.Text type="secondary">
                    {t('No new tab?')}{' '}
                    <a
                        href={connection.redirectUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('Open the sign-in page')}
                    </a>
                </Typography.Text>
            )}
            {catalog.isError && (
                <Alert
                    type="error"
                    message={t('Could not load apps')}
                />
            )}
            <List
                className="max-h-96 overflow-y-auto"
                loading={catalog.isPending}
                loadMore={
                    <LoadMoreOnScroll
                        hasMore={!!catalog.hasNextPage}
                        loading={catalog.isFetchingNextPage}
                        onLoad={() => void catalog.fetchNextPage()}
                    />
                }
                dataSource={catalog.data?.pages.flatMap((page) => page.items) ?? []}
                renderItem={(app) => (
                    <CatalogAppItem
                        app={app}
                        connected={connected.has(app.slug)}
                        connecting={connection.isPending}
                        onConnect={() => connect(app.slug)}
                    />
                )}
            />
        </Flex>
    )
}

type CatalogAppItemProps = {
    app: AppToolkit
    connected: boolean
    connecting: boolean
    onConnect: () => void
}

function CatalogAppItem({ app, connected, connecting, onConnect }: CatalogAppItemProps) {
    const { t } = useTranslation()

    const action = connected ? (
        <Tag
            key="connected"
            color="green"
            className="!m-0"
        >
            {t('Connected')}
        </Tag>
    ) : (
        <Button
            key="connect"
            disabled={connecting}
            onClick={onConnect}
        >
            {t('Connect')}
        </Button>
    )

    return (
        <List.Item actions={[action]}>
            <List.Item.Meta
                avatar={
                    <AppIcon
                        name={app.name}
                        toolkit={app.slug}
                        logo={app.logo}
                    />
                }
                title={app.name}
                description={
                    <Typography.Paragraph
                        type="secondary"
                        className="!mb-0"
                        ellipsis={{ rows: 2 }}
                    >
                        {app.description}
                    </Typography.Paragraph>
                }
            />
        </List.Item>
    )
}

export function useDebouncedSearch(value: string) {
    const [search, setSearch] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => setSearch(value.trim()), 300)

        return () => clearTimeout(timer)
    }, [value])

    return search
}
